import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { MessageSquare, CheckCircle, XCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const SlackIntegration = ({ reducedMotion }) => {
  const [connected, setConnected] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [channel, setChannel] = useState('#analytics');
  const [testingSend, setTestingSend] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/integrations/slack`);
      const data = await response.json();
      setConnected(data.connected || false);
      if (data.webhook_url) setWebhookUrl(data.webhook_url);
      if (data.channel) setChannel(data.channel);
    } catch (error) {
      console.error('Failed to check Slack connection:', error);
    }
  };

  const handleConnect = async () => {
    if (!webhookUrl) {
      toast.error('Please enter a Slack webhook URL');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/integrations/slack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ webhook_url: webhookUrl, channel })
      });
      if (response.ok) {
        setConnected(true);
        toast.success('Slack connected successfully');
      } else {
        toast.error('Failed to connect Slack');
      }
    } catch (error) {
      toast.error('Connection failed');
    }
  };

  const handleDisconnect = () => {
    setConnected(false);
    setWebhookUrl('');
    toast.info('Slack disconnected');
  };

  const handleTestMessage = async () => {
    setTestingSend(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Test message sent to Slack!');
    } catch (error) {
      toast.error('Failed to send test message');
    } finally {
      setTestingSend(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="slack-integration-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-ink-500" />
              Slack Integration
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              Send real-time alerts and reports to Slack channels
            </CardDescription>
          </div>
          <Badge
            className={connected ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}
            data-testid="connection-status"
          >
            {connected ? (
              <><CheckCircle className="w-3 h-3 mr-1" /> Connected</>
            ) : (
              <><XCircle className="w-3 h-3 mr-1" /> Disconnected</>
            )}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {!connected ? (
            <>
              <div>
                <label className="text-xs text-slate-400 mb-2 block">Slack Webhook URL</label>
                <Input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/..."
                  className="bg-slate-800 border-slate-700"
                  data-testid="webhook-url-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-2 block">Default Channel</label>
                <Input
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  placeholder="#analytics"
                  className="bg-slate-800 border-slate-700"
                  data-testid="channel-input"
                />
              </div>
              <Button
                onClick={handleConnect}
                className="w-full bg-ink-700 hover:bg-ink-700 text-white"
                data-testid="connect-button"
              >
                Connect to Slack
              </Button>
              <div className="p-3 bg-ink-500 rounded-lg border border-ink-300 dark:border-ink-700">
                <p className="text-xs text-slate-400">
                  <strong className="text-ink-500">How to get a webhook URL:</strong><br />
                  1. Go to Slack Apps → Incoming Webhooks<br />
                  2. Create a new webhook<br />
                  3. Copy the webhook URL and paste it above
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-emerald-400 mb-1">Connected to Slack</p>
                    <p className="text-xs text-slate-400">Alerts will be sent to <strong>{channel}</strong></p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleTestMessage}
                  disabled={testingSend}
                  className="flex-1 bg-ink-700 hover:bg-ink-700 text-white"
                  data-testid="test-message-button"
                >
                  {testingSend ? (
                    <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Sending...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Send Test Message</>
                  )}
                </Button>
                <Button
                  onClick={handleDisconnect}
                  variant="outline"
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="disconnect-button"
                >
                  Disconnect
                </Button>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-300">Alert Types</h4>
                <div className="space-y-1">
                  {['High error rate detected', 'Answer rate below threshold', 'Campaign performance milestone'].map((alert, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-400" data-testid={`alert-type-${i}`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-ink-700" />
                      {alert}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SlackIntegration;